import { useCallback, useRef, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import { CheckSquare } from 'lucide-react';
import { can, type Membership } from '@kratispot/shared';
import type {
  BookingProblem,
  FloorStatus,
  MapReservation,
  MapSpot,
  Reservation,
  ReservationRequest,
} from '@kratispot/shared';
import { CalendarView } from '@/components/calendar-view';
import { DayStrip } from '@/components/day-strip';
import { ListView } from '@/components/list-view';
import { ViewTabs, type VenueView } from '@/components/view-tabs';
import { PointerNote, type PointerNoteAt } from '@/components/hover-note';
import { ExtendDialog } from '@/components/map/extend-dialog';
import { ratesOf } from '@/components/map/reserve-form';
import { ReserveModal } from '@/components/map/reserve-modal';
import { SelectBar } from '@/components/map/select-bar';
import { SpotDialog } from '@/components/map/spot-dialog';
import { guestNoteOf } from '@/components/map/spot-tile';
import { VenueMap } from '@/components/map/venue-map';
import { LiveIndicator } from '@/components/live-indicator';
import { Toasts, useToasts } from '@/components/toasts';
import { Button } from '@/components/ui/button';
import { useDragSelect } from '@/hooks/use-drag-select';
import { useCalendar } from '@/hooks/use-calendar';
import { useReservationCommands } from '@/hooks/use-reservation-commands';
import { useSpotCommands } from '@/hooks/use-spot-commands';
import { useVenueMap } from '@/hooks/use-venue-map';
import { useVenueSocket } from '@/hooks/use-venue-socket';
import { useTranslation } from '@/lib/i18n';
import { changedMessage, refusedMessage, sweptSelection } from '@/lib/map-messages';
import { cn } from '@/lib/utils';

const PULSE_MS = 3600;

const PROBLEM_COPY = { 'spot-booked': 'spotBooked', 'spot-in-checkout': 'spotInCheckout' } as const;

// Everything already in the address, so moving the day keeps Select mode on. §8
export function paramsOf(query: URLSearchParams): Record<string, string> {
  return Object.fromEntries(query.entries());
}

const TOAST_TONE: Readonly<Record<FloorStatus, string>> = {
  free: 'bg-emerald-600',
  occupied: 'bg-red-500',
  cleaning: 'bg-sky-500',
};

// The venue's own screen: the day strip, the floor, and nothing else. §15
export function VenuePage({
  venueId,
  membership,
}: {
  venueId: string;
  membership: Membership;
}): ReactElement {
  const { t, language } = useTranslation();
  // What the floor offers is what this member may actually do. §4
  const owner = can(membership, 'venue:settings');
  const mayMove = can(membership, 'spot:status');
  const mayBook = can(membership, 'reservation:write');
  // §8: the day is part of the address, so a reload keeps the day staff were on.
  const [query, setQuery] = useSearchParams();
  const date = query.get('date');
  const setDate = useCallback(
    (day: string) => setQuery((was) => ({ ...paramsOf(was), date: day })),
    [setQuery],
  );
  const [view, setView] = useState<VenueView>('map');
  const { state, reload, apply } = useVenueMap(venueId, date);
  const [openSpot, setOpenSpot] = useState<string | null>(null);
  // Settings counts the staff-only spots and sends the owner here to edit. §11
  const [selecting, setSelecting] = useState(query.get('select') === '1');
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  // The bar counts as the finger sweeps; the map itself waits for pointer-up.
  const [sweeping, setSweeping] = useState<number | null>(null);
  const [pulsing, setPulsing] = useState<ReadonlySet<string>>(new Set());
  const [focus, setFocus] = useState<ReadonlySet<string>>(new Set());
  const [note, setNote] = useState<PointerNoteAt | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // The day the dialog opens on travels with it: a reload must not move it.
  const [booking, setBooking] = useState<{ spots: readonly MapSpot[]; date: string } | null>(null);
  const [extending, setExtending] = useState<{ booking: MapReservation; spot: MapSpot } | null>(
    null,
  );
  // §10: what a refused booking said, kept on the screen that was refused.
  const [refused, setRefused] = useState<string | null>(null);
  const [reads, setReads] = useState(0);
  const { toasts, push } = useToasts();
  // The server names the reason; the floor says it in the language on screen. §19
  const refusalMessage = useCallback(
    (problem: BookingProblem | null): string =>
      refusedMessage(problem, language, {
        reason: (each) => t(PROBLEM_COPY[each.reason]),
        failed: t('actionFailed'),
        onDays: t('onDays'),
      }),
    [t, language],
  );
  const refresh = useCallback(() => {
    setReads((count) => count + 1);
    reload();
  }, [reload]);
  const commands = useSpotCommands(venueId, apply, reload);
  const bookings = useReservationCommands(venueId, apply, refresh);

  const pickedRef = useRef(picked);
  pickedRef.current = picked;

  const drag = useDragSelect({
    enabled: selecting,
    isPicked: useCallback((spotId) => pickedRef.current.has(spotId), []),
    onProgress: useCallback(
      (touched: ReadonlySet<string>, adding: boolean) =>
        setSweeping(sweptSelection(pickedRef.current, touched, adding).size),
      [],
    ),
    onCommit: useCallback((spotIds: readonly string[], adding: boolean) => {
      setSweeping(null);
      setPicked((current) => sweptSelection(current, spotIds, adding));
    }, []),
  });

  // A sweep has already applied its tiles; the click it leaves behind is noise.
  const pick = useCallback(
    (spot: MapSpot): void => {
      if (drag.claimClick()) return;
      if (!selecting) {
        setOpenSpot(spot.id);
        return;
      }
      setPicked((current) => {
        const next = new Set(current);
        if (!next.delete(spot.id)) next.add(spot.id);
        return next;
      });
    },
    [drag, selecting],
  );

  // Only a booking has anything to add; a free tile says all it has on its face.
  const hover = useCallback((spot: MapSpot, at: { x: number; y: number } | null) => {
    const label = guestNoteOf(spot);
    setNote(at === null || label === null ? null : { label, x: at.x, y: at.y });
  }, []);

  const pulse = useCallback((spots: readonly MapSpot[]) => {
    const ids = new Set(spots.map((spot) => spot.id));
    setPulsing(ids);
    timers.current.push(setTimeout(() => setPulsing(new Set()), PULSE_MS));
  }, []);

  // What THIS device did: pulse it, and keep it on screen. The socket's own
  // changes only pulse — another hand must not move the map under this one.
  const acted = useCallback(
    (spots: readonly MapSpot[]) => {
      pulse(spots);
      setFocus(new Set(spots.map((spot) => spot.id)));
    },
    [pulse],
  );

  // A live day patches in place; any other day re-reads: a booking may be gone.
  const live = useVenueSocket(venueId, {
    onChange: useCallback(
      (change) => {
        // A change with no tiles is a re-read: a config edit, or a booking made
        // for a day this floor is not showing. Patching nothing leaves it stale. §16
        const live = state.phase === 'ready' && state.map.mode === 'live';
        const patchable = change.reason !== 'settings' && live && change.spots.length > 0;
        if (patchable) apply(change.spots);
        else reload();
        setReads((count) => count + 1);
        pulse(change.spots);
        // Nobody on this floor did it, so the deadline says so itself. §10
        if (change.reason === 'auto-release')
          push(changedMessage(change.spots, t('holdExpired'), t('holdsExpired')), 'bg-amber-500');
      },
      [state, apply, reload, pulse, push, t],
    ),
    onResync: reload,
  });

  const day = state.phase === 'ready' ? state.map : null;
  const calendar = useCalendar(
    venueId,
    day?.date ?? '',
    day?.today ?? '',
    view === 'calendar' && day !== null,
    reads,
  );

  if (state.phase !== 'ready')
    return (
      <p
        role={state.phase === 'loading' ? undefined : 'alert'}
        className="p-6 text-center text-sm text-muted-foreground"
      >
        {t(
          state.phase === 'loading'
            ? 'loading'
            : state.phase === 'no-layout'
              ? 'mapNoLayout'
              : 'errorGeneric',
        )}
      </p>
    );

  const map = state.map;
  const spotById = new Map(map.spots.map((spot) => [spot.id, spot]));

  const panelSpot = openSpot === null ? undefined : spotById.get(openSpot);
  const selectable = map.mode !== 'past' && (mayMove || mayBook || owner);

  // Every action closes what opened it; the toast and the pulse confirm it. §7
  async function move(spots: readonly MapSpot[], status: FloorStatus): Promise<void> {
    setOpenSpot(null);
    setSelecting(false);
    setPicked(new Set());
    const ok = await commands.changeStatus(spots, status);
    acted(spots);
    const done = changedMessage(spots, t('statusChanged'), t('bulkChanged'));
    push(
      ok ? done : refusalMessage(commands.lastProblem()),
      ok ? TOAST_TONE[status] : 'bg-red-500',
    );
  }

  async function keepForStaff(spots: readonly MapSpot[], staffOnly: boolean): Promise<void> {
    setOpenSpot(null);
    setSelecting(false);
    setPicked(new Set());
    const ok = await commands.changeStaffOnly(spots, staffOnly);
    acted(spots);
    push(
      ok ? t('staffOnlyChanged') : refusalMessage(commands.lastProblem()),
      ok ? 'bg-amber-500' : 'bg-red-500',
    );
  }

  function pickedSpots(): MapSpot[] {
    return [...picked].map((id) => spotById.get(id) as MapSpot);
  }

  function closeEverything(): void {
    setRefused(null);
    setOpenSpot(null);
    setSelecting(false);
    setPicked(new Set());
    setBooking(null);
    setExtending(null);
  }

  // §10: the spot the server named comes off the selection, and the rest go.
  function dropFromBooking(from: { spots: readonly MapSpot[]; date: string }, spot: MapSpot): void {
    setRefused(null);
    setBooking({ ...from, spots: from.spots.filter((each) => each.id !== spot.id) });
    setPicked((current) => {
      const next = new Set(current);
      next.delete(spot.id);
      return next;
    });
  }

  // §10: a refused booking keeps the screen, so a date can be changed on it.
  async function reserve(spots: readonly MapSpot[], request: ReservationRequest): Promise<void> {
    setRefused(null);
    const made = await bookings.create(request);
    if (made === null) {
      setRefused(refusalMessage(bookings.lastProblem()));
      return;
    }
    closeEverything();
    acted(spots);
    push(t('reserveSaved'), 'bg-amber-500');
  }

  // A booking action names what it did, and offers to take it back. §10
  async function actOn(
    reservation: MapReservation,
    action: 'cancel' | 'check-in',
    spot: MapSpot,
  ): Promise<void> {
    closeEverything();
    const done = await bookings.act(reservation.id, action);
    acted([spot]);
    if (done === null) {
      push(t('actionFailed'), 'bg-red-500');
      return;
    }
    push(
      t(action === 'cancel' ? 'resCancelled' : 'checkedIn'),
      'bg-emerald-600',
      () => void undo(reservation, spot),
    );
  }

  async function undo(reservation: Reservation | MapReservation, spot: MapSpot): Promise<void> {
    const back = await bookings.act(reservation.id, 'restore');
    acted([spot]);
    push(back === null ? t('restoreFailed') : t('restored'), 'bg-emerald-600');
  }

  async function extend(reservation: MapReservation, holdMinutes: number): Promise<void> {
    closeEverything();
    const done = await bookings.extend(reservation.id, holdMinutes);
    push(done === null ? t('actionFailed') : t('extended'), 'bg-amber-500');
  }

  return (
    <div className="flex flex-1 flex-col gap-3">
      <DayStrip date={map.date} today={map.today} mode={map.mode} onPick={setDate} />
      <ViewTabs view={view} onPick={setView} />
      {view === 'list' && <ListView map={map} onOpen={(spot) => setOpenSpot(spot.id)} />}
      {view === 'calendar' && (
        <CalendarView
          map={map}
          bookings={calendar}
          onPickDay={setDate}
          onReserve={
            mayBook
              ? (spotId, on) => {
                  setDate(on);
                  setBooking({ spots: map.spots.filter((spot) => spot.id === spotId), date: on });
                }
              : null
          }
        />
      )}
      <div className={cn('relative px-2 pb-4 sm:px-3', view !== 'map' && 'hidden')}>
        <span className="absolute start-5 top-2 z-6 sm:start-6">
          <LiveIndicator state={live} />
        </span>
        {selectable && (
          <Button
            size="sm"
            variant={selecting ? 'default' : 'outline'}
            className={cn('absolute end-5 top-2 z-6 rounded-full sm:end-6')}
            aria-pressed={selecting}
            onClick={() => {
              setSelecting(!selecting);
              setPicked(new Set());
            }}
          >
            <CheckSquare className="size-4" aria-hidden />
            {t('select')}
          </Button>
        )}
        <VenueMap
          map={map}
          selected={picked}
          pulsing={pulsing}
          focus={focus}
          painting={selecting}
          onPick={pick}
          onPointerDown={drag.onPointerDown}
          onHover={hover}
        />
      </div>

      {panelSpot !== undefined && !selecting && (
        <SpotDialog
          spot={panelSpot}
          venue={map.venue}
          mode={map.mode}
          date={map.date}
          today={map.today}
          nowMinutes={map.nowMinutes}
          busy={commands.busy || bookings.busy}
          membership={membership}
          onClose={() => setOpenSpot(null)}
          onStatus={(status) => void move([panelSpot], status)}
          onStaffOnly={(staffOnly) => void keepForStaff([panelSpot], staffOnly)}
          refused={refused}
          onReserve={(request) => void reserve([panelSpot], request)}
          onCheckIn={(each) => void actOn(each, 'check-in', panelSpot)}
          onCancelBooking={(each) => void actOn(each, 'cancel', panelSpot)}
          onExtend={(each) => {
            setOpenSpot(null);
            setExtending({ booking: each, spot: panelSpot });
          }}
        />
      )}

      {booking !== null && (
        <ReserveModal
          venue={map.venue}
          spots={booking.spots}
          date={booking.date}
          today={map.today}
          nowMinutes={map.nowMinutes}
          busy={bookings.busy}
          onClose={() => setBooking(null)}
          refused={refused}
          onDropSpot={(spot) => dropFromBooking(booking, spot)}
          onSubmit={(request) => void reserve(booking.spots, request)}
        />
      )}

      {extending !== null && (
        <ExtendDialog
          venue={map.venue}
          booking={extending.booking}
          ratePerHourCents={ratesOf(map.venue, [extending.spot])[0] as number}
          today={map.today}
          nowMinutes={map.nowMinutes}
          busy={bookings.busy}
          onClose={() => setExtending(null)}
          onExtend={(holdMinutes) => void extend(extending.booking, holdMinutes)}
        />
      )}

      {selecting && (
        <SelectBar
          count={sweeping ?? picked.size}
          mode={map.mode}
          busy={commands.busy}
          membership={membership}
          anyOnline={pickedSpots().some((spot) => !spot.staffOnly)}
          payingNumbers={pickedSpots()
            .filter((spot) => spot.pendingCheckout)
            .map((spot) => spot.number)}
          onStatus={(status) => void move(pickedSpots(), status)}
          onStaffOnly={(staffOnly) => void keepForStaff(pickedSpots(), staffOnly)}
          onReserve={() => setBooking({ spots: pickedSpots(), date: map.date })}
          onClose={() => {
            setSelecting(false);
            setPicked(new Set());
          }}
        />
      )}
      <PointerNote note={note} />
      <Toasts toasts={toasts} undoLabel={t('undo')} />
    </div>
  );
}
